/* eslint-disable no-param-reassign */
import React, { useContext, useState, useEffect } from 'react';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useDataModel, params, client, extend } from '@thd-nucleus/data-sources';
import { bool, string, shape } from 'prop-types';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType,
} from '@thd-nucleus/data-sources/react/dataModel/prop-types';
import {
  dataModel as deliveryDataModel,
  DeliveryOptions
} from '@thd-olt-component-react/delivery-options';
import { FulfilmentPodPlaceholder } from './FulfilmentPodPlaceholder';
import { getShippingTemplate } from '../helpers/shipping-helper';
import {
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  isPodShippingEqual
} from '../helpers/pod-fulfillment-utils';
import { EmptyShippingMessage } from '../partials/shipping/EmptyShippingMessage';
import './fulfillment-pod-shipping.scss';
import {
  isShowBoth,
  isTwoTile,
  isTwoTileOff,
  isMajorApplianceProductType,
  getUrlForApplianceData,
  getDeliveryZip
} from '../../components/helper/utils';
import { renderDeliveryTemplates } from '../templates/deliveryTemplate';

export const FulfillmentPodShipping = React.memo(({
  itemId, storeId, deliveryZip, isTwoTileEnabledFS, useCustomTemplate
}) => {
  const { storeId: useStoreId } = useStore();
  const { customer } = useContext(ExperienceContext);
  const [product, setProduct] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const isTwoTileMessagingEnabled = useConfigService('fs-prop:product-pod__fulfillment--isTwoTileMessagingEnabled');

  if ((isTwoTile() || isShowBoth()) && !isTwoTileOff()) {
    isTwoTileEnabledFS = true;
  }
  if (!isTwoTileEnabledFS) {
    isTwoTileEnabledFS = isTwoTileEnabledGlobal && isTwoTileMessagingEnabled;
  }
  const opts = {
    variables: {
      itemId,
      storeId: storeId || useStoreId,
      zipCode: deliveryZip
    },
    skip: !itemId,
    ssr: false
  };

  const { data, loading, error } = useDataModel('product', opts);
  const { product: productData } = data || {};

  const fetchAvailabilityData = async () => {
    let urlOrigin = '';
    if (typeof window !== 'undefined') {
      urlOrigin = window?.location?.origin;
    }

    const zipCode = getDeliveryZip();
    const url = getUrlForApplianceData(itemId, [], zipCode, urlOrigin);

    const fetchData = await fetch(url);
    const response = await fetchData.json();
    setAvailabilityData(response);
    setIsLoadingAvailability(false);
  };

  useEffect(() => {
    if (productData) {
      setProduct(productData);
      setAvailabilityData(null);
    }
  }, [productData]);

  const isHDQC = product?.info?.productSubType?.name === 'HDQC';
  const { scheduleAMeasure = false } = product?.installServices || {};
  const isScheduleAConsultationEligible = isCustomKitchenCabinetProduct(product);
  const isRequestAQuoteEligible = isBrioProduct(product);
  if (scheduleAMeasure
    || isScheduleAConsultationEligible
    || isRequestAQuoteEligible
    || isHDQC) {
    return null;
  }

  const ShippingComponent = getShippingTemplate(
    {
      availabilityData,
      product,
      customer,
      isTwoTileEnabledFS,
      customRender: useCustomTemplate ? renderDeliveryTemplates : null
    }
  );
  if (!ShippingComponent) { return EmptyShippingMessage; }

  if (loading || (
    typeof (data) === 'undefined' && !error && !product
  )) return <FulfilmentPodPlaceholder />;
  if (!product) return null;
  if (isLoadingAvailability) {
    return (
      <div className="loading-spinner">
        <span className="segment-spinner" />
      </div>
    );
  }

  const isMajorAppliance = isMajorApplianceProductType(data?.product?.identifiers?.productType);
  const showCheckAvailabilityButton = isMajorAppliance && !availabilityData;
  return (
    <>
      <ShippingComponent
        availabilityData={availabilityData}
        product={product}
        isTwoTileEnabledFS={isTwoTileEnabledFS}
      />
      {showCheckAvailabilityButton && (
        <button
          type="button"
          className="check-availability-button u__default-link"
          role="link"
          onClick={() => {
            setIsLoadingAvailability(true);
            fetchAvailabilityData();
          }}
        >
          Check Earliest Availability Date
        </button>
      )}
    </>
  );

}, isPodShippingEqual);

FulfillmentPodShipping.propTypes = {
  itemId: string.isRequired,
  storeId: string,
  useCustomTemplate: bool,
  isTwoTileEnabledFS: bool,
  deliveryZip: string,
};

FulfillmentPodShipping.defaultProps = {
  useCustomTemplate: false,
  isTwoTileEnabledFS: false,
  storeId: undefined,
  deliveryZip: undefined
};

const installServices = params({ storeId: stringType(), zipCode: stringType() }).shape({
  scheduleAMeasure: boolType()
});
if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

const Service = shapeType({
  deliveryTimeline: stringType(),
  deliveryDates: shapeType({
    startDate: stringType(),
    endDate: stringType(),
  }),
  deliveryCharge: stringType(),
  dynamicEta: shapeType({
    hours: stringType(),
    minutes: stringType()
  }),
  hasFreeShipping: boolType(),
  freeDeliveryThreshold: numberType({ float: true }),
  locations: arrayOfType(shapeType({
    curbsidePickupFlag: boolType(),
    isBuyInStoreCheckNearBy: boolType(),
    distance: numberType({ float: true }),
    inventory: shapeType({
      isOutOfStock: boolType(),
      isInStock: boolType(),
      isLimitedQuantity: boolType(),
      isUnavailable: boolType(),
      quantity: numberType(),
      maxAllowedBopisQty: numberType(),
      minAllowedBopisQty: numberType()
    }),
    isAnchor: boolType(),
    locationId: stringType(),
    state: stringType(),
    storeName: stringType(),
    storePhone: stringType(),
    type: stringType(),
  })),
  type: stringType(),
  totalCharge: numberType({ float: true }),
});

FulfillmentPodShipping.dataModel = extend({
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  })
    .shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        productType: stringType(),
      }),
      info: shapeType({
        classNumber: stringType(),
        hasSubscription: boolType(),
        isLiveGoodsProduct: boolType(),
        productDepartment: stringType(),
        productSubType: shapeType({
          name: stringType()
        }),
        globalCustomConfigurator: shapeType({
          customExperience: stringType(),
        }),
        isBrioSku: boolType(),
        isCustomKitchenCabinet: boolType(),
        isGenericProduct: boolType()
      }),
      pricing: params({ storeId: stringType() })
        .shape({
          original: numberType({ float: true }),
          value: numberType({ float: true })
        }),
      availabilityType: shapeType({
        buyable: boolType(),
        discontinued: boolType(),
        status: boolType(),
        type: stringType(),

      }),
      fulfillment: client(params({
        storeId: stringType(), zipCode: stringType(),
      }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shapeType({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(Service)
        })),
        onlineStoreStatus: boolType(),
        onlineStoreStatusType: stringType(),
      })),
      installServices
    }),
  shipping: deliveryDataModel.shipping
}, DeliveryOptions);

FulfillmentPodShipping.displayName = 'FilfillmentPodShipping';
